import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { RolesListReportData } from '~/data/reports';
import { Roles, getRoleTranslationKeys } from '~/data/user/role';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common/useReportNavigateToView';
import {
  ReportResponsibleColumnWidths,
  RolesListReportRow,
  RolesListReportRowResponsibleUnitDetails,
  convertRolesListReportDataToRows,
  getColumnWidths,
} from '~/scenes/PortalModules/Analytics/People/RolesListReport/data';

type PropsType = {
  data: RolesListReportData[];
};

export const useRolesListReport = (props: PropsType) => {
  const { data } = props;

  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { orgUnits } = useAllOrgUnits();

  const { rows, columnWidths } = useMemo(() => {
    const getRoleTranslations = (roles: Roles[]) =>
      getRoleTranslationKeys(roles).map((key) => t(key));

    const rows = convertRolesListReportDataToRows({
      data,
      getRoleTranslations,
      orgUnits,
    });

    const columnWidths = getColumnWidths(rows);

    return {
      rows,
      columnWidths,
    };
  }, [data, t, orgUnits]);

  const { columns, columnGroupingModel } = useMemo(() => {
    const employeeColumns: GridColDef<RolesListReportRow>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        valueGetter: (_value, row) =>
          getEmployeeById(row.employeeId)?.fEmpPersonNumber,
        minWidth: 130,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'unitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'userRoles',
        headerName: t('table_col_roles'),
        minWidth: 150,
        width: columnWidths.roles,
      },
    ];

    const getRoleResponsibleColumnConfigs = (
      role: string,
      colWidths: ReportResponsibleColumnWidths,
      getResponsibleUnitDetails: (
        row: RolesListReportRow,
      ) => RolesListReportRowResponsibleUnitDetails,
    ): GridColDef<RolesListReportRow>[] => {
      return [
        {
          field: `${role}ResponsibleUnitNames`,
          headerName: t('laOrgUnit'),
          minWidth: 180,
          width: colWidths.names,
          valueGetter: (_value, row) => getResponsibleUnitDetails(row).names,
        },
        {
          field: `${role}ResponsibleUnitNumbers`,
          headerName: t('laUnitNumber'),
          minWidth: 150,
          width: colWidths.numbers,
          valueGetter: (_value, row) => getResponsibleUnitDetails(row).numbers,
        },
      ];
    };

    const hrColumns = getRoleResponsibleColumnConfigs(
      'hr',
      columnWidths.hrUnits,
      (row) => row.responsibleUnits.hr,
    );
    const managerColumns = getRoleResponsibleColumnConfigs(
      'manager',
      columnWidths.managerUnits,
      (row) => row.responsibleUnits.manager,
    );
    const altManagerColumns = getRoleResponsibleColumnConfigs(
      'altManager',
      columnWidths.altManagerUnits,
      (row) => row.responsibleUnits.alternateManager,
    );
    const deputyManagerColumns = getRoleResponsibleColumnConfigs(
      'deputyManager',
      columnWidths.deputyManagerUnits,
      (row) => row.responsibleUnits.deputyManager,
    );

    const columns: GridColDef<RolesListReportRow>[] = [
      ...employeeColumns,
      ...hrColumns,
      ...managerColumns,
      ...altManagerColumns,
      ...deputyManagerColumns,
    ];

    const roleGroupHeaderName = (role: string) => {
      return `${role}: ${t('laResponsibleOf')}`;
    };

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumns),
      },
      {
        groupId: 'hr',
        headerName: roleGroupHeaderName(t('laHR')),
        children: getFieldNamesFromColumnConfig(hrColumns),
      },
      {
        groupId: 'manager',
        headerName: roleGroupHeaderName(t('laManager')),
        children: getFieldNamesFromColumnConfig(managerColumns),
      },
      {
        groupId: 'altManager',
        headerName: roleGroupHeaderName(t('laAltManager')),
        children: getFieldNamesFromColumnConfig(altManagerColumns),
      },
      {
        groupId: 'deputyManager',
        headerName: roleGroupHeaderName(t('laDeputyManager')),
        children: getFieldNamesFromColumnConfig(deputyManagerColumns),
      },
    ];

    return {
      columns,
      columnGroupingModel,
    };
  }, [getEmployeeById, goToEmployeeBasicDataView, t, columnWidths]);

  return {
    rows,
    columns,
    columnGroupingModel,
  };
};
